/**
 * SPD Data Processing Utilities
 *
 * This file contains all data transformation logic for the Summary Plan Description component.
 * It handles field name normalization, data deduplication, section organization, and status derivation.
 */

/**
 * Section configuration defining how fields are organized
 */
export const SECTION_CONFIG = [
  {
    id: 'overview',
    title: 'Plan Overview',
    icon: 'document',
    fields: [
      'plantype', 'planadministrator', 'contributiontaxtreatment',
      'contributionlimit', 'catchupcontributionlimit',
      'age6063catchupcontributions', 'specialcatchup',
      'contributionsources', 'employeecontribution',
      'employercontribution', 'roth403b', 'roth457b',
      'planyear', 'planeffectivedate', 'writtenplaneffectivedate',
      'automaticenrollment', 'eligibleemployees',
      'excludedemployees', 'excludedemployess', 'beneficiaries'
    ]
  },
  {
    id: 'transfers',
    title: 'Transfers & Exchanges',
    icon: 'arrows',
    fields: [
      'exchangesinplan', 'transfersintoplan', 'transfersoutofplan',
      'rolloversintoplan', 'rolloversoutofplan'
    ]
  },
  {
    id: 'distributions',
    title: 'Distributions & Access',
    icon: 'dollar',
    fields: [
      'distributions', 'loans', 'hardship', 'automaticdistributions',
      'disability', 'unforeseeableemergencydistributions'
    ]
  }
];

/**
 * Normalizes a field key for consistent matching
 * Strips spaces, hyphens, underscores, colons and converts to lowercase
 *
 * @param {string} name - The field name to normalize
 * @returns {string} - Normalized key
 */
export const normalizeFieldKey = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .replace(/[\s\-_:]/g, '') // Remove spaces, hyphens, underscores, colons
    .toLowerCase();
};

/**
 * Derives availability status from field value text
 *
 * @param {string} value - The field value text
 * @returns {string|null} - 'available', 'unavailable', 'partial', or null
 */
export const deriveStatus = (value) => {
  if (!value || typeof value !== 'string') return null;

  const lowerValue = value.toLowerCase().trim();

  // Check for unavailable status
  if (lowerValue === 'not available' ||
      lowerValue === 'n/a' ||
      lowerValue === 'na' ||
      lowerValue.startsWith('not available')) {
    return 'unavailable';
  }

  // Check for available with conditions (partial)
  if (lowerValue.includes('available')) {
    // Look for conditional keywords
    const conditionalKeywords = ['if', 'only', 'subject to', 'provided', 'when', 'unless', 'except'];
    const hasCondition = conditionalKeywords.some(keyword => lowerValue.includes(keyword));

    if (hasCondition) {
      return 'partial';
    }

    // Simple "available" without conditions
    if (lowerValue === 'available' || lowerValue === 'yes') {
      return 'available';
    }

    // "Available" at start but with more text - likely conditional
    if (lowerValue.startsWith('available') && lowerValue.length > 'available'.length + 5) {
      return 'partial';
    }

    return 'available';
  }

  return null;
};

/**
 * Filters out internal/admin fields that should not be displayed
 *
 * @param {string} normalizedKey - The normalized field key
 * @returns {boolean} - True if field should be filtered out
 */
export const shouldFilterInternalField = (normalizedKey) => {
  const internalFields = [
    'planpassword',
    'clientid',
    'plangroup',
    'searchavailable',
    'searchenroll',
    'searchforms',
    'showpayrolldates',
    'showspd',
    'deletedate',
    'lastupdateddate',
    'logotouse',
    'plandescname',
    'plandescrname',
    'plandescriptionname',
    'summaryplandescription(spd)',
    'summaryplandescription',
    'id',
    'employerid',
    'planid',
    'lastupdatedt'
  ];

  // Check exact matches
  if (internalFields.includes(normalizedKey)) {
    return true;
  }

  // Check prefix patterns
  if (normalizedKey.startsWith('testlabel') ||
      normalizedKey.startsWith('test_') ||
      normalizedKey.startsWith('_')) {
    return true;
  }

  return false;
};

/**
 * Combines and deduplicates data from universal and plan-specific arrays
 * Normalizes field keys and derives status for each item
 *
 * @param {Array} universalData - Array of universal plan data items
 * @param {Array} planData - Array of plan-specific data items
 * @returns {Array} - Combined and deduplicated array with normalized keys
 */
export const combineAndDeduplicateData = (universalData = [], planData = []) => {
  const dataMap = new Map();

  // Process universal data first (uses 'fieldName' property)
  universalData.forEach(item => {
    const fieldName = item.fieldName || item.name;
    if (!fieldName || !item.value) return;

    const normalizedKey = normalizeFieldKey(fieldName);
    if (!normalizedKey) return;

    dataMap.set(normalizedKey, {
      normalizedKey,
      fieldNameReadable: item.fieldNameReadable || fieldName,
      value: item.value,
      status: deriveStatus(item.value)
    });
  });

  // Process plan data - overwrite if value is more substantial (uses 'field' property)
  planData.forEach(item => {
    const fieldName = item.field || item.fieldName || item.name;
    if (!fieldName) return;

    const normalizedKey = normalizeFieldKey(fieldName);
    if (!normalizedKey) return;

    const existingItem = dataMap.get(normalizedKey);
    const newValue = item.value || '';

    // Prefer non-empty values, or plan-specific data if equal
    if (!existingItem ||
        (newValue && newValue.trim().length > 0) ||
        (existingItem.value && existingItem.value.trim().length === 0 && newValue.trim().length === 0)) {
      dataMap.set(normalizedKey, {
        normalizedKey,
        fieldNameReadable: item.fieldNameReadable || fieldName,
        value: newValue,
        status: deriveStatus(newValue)
      });
    }
  });

  return Array.from(dataMap.values());
};

/**
 * Organizes combined data into sections based on configuration
 * Creates an "Other Information" section for unmapped fields
 *
 * @param {Array} combinedData - Combined and deduplicated data array
 * @param {Array} sectionConfig - Section configuration array
 * @returns {Array} - Array of section objects with items
 */
export const organizeDataIntoSections = (combinedData, sectionConfig = SECTION_CONFIG) => {
  const sections = [];
  const mappedKeys = new Set();

  // Process configured sections
  sectionConfig.forEach(section => {
    const items = [];

    section.fields.forEach(fieldKey => {
      const normalizedFieldKey = normalizeFieldKey(fieldKey);
      const item = combinedData.find(d => d.normalizedKey === normalizedFieldKey);

      if (item && !shouldFilterInternalField(item.normalizedKey)) {
        // Only include items with non-empty values
        if (item.value && item.value.trim().length > 0) {
          items.push(item);
          mappedKeys.add(item.normalizedKey);
        }
      }
    });

    // Only add section if it has items
    if (items.length > 0) {
      sections.push({
        id: section.id,
        title: section.title,
        icon: section.icon,
        items
      });
    }
  });

  // Create "Other Information" section for unmapped fields
  const otherItems = combinedData.filter(item => {
    if (shouldFilterInternalField(item.normalizedKey)) return false;
    if (mappedKeys.has(item.normalizedKey)) return false;
    if (!item.value || item.value.trim().length === 0) return false;
    return true;
  });

  if (otherItems.length > 0) {
    sections.push({
      id: 'other',
      title: 'Other Information',
      icon: 'info',
      items: otherItems
    });
  }

  return sections;
};

/**
 * Extracts key statistics from combined data
 *
 * @param {Array} combinedData - Combined and deduplicated data array
 * @returns {Object} - Object with contributionLimit, taxTreatment, planTypeValue
 */
export const extractStatsData = (combinedData) => {
  const stats = {
    contributionLimit: null,
    taxTreatment: null,
    planTypeValue: null
  };

  combinedData.forEach(item => {
    switch (item.normalizedKey) {
      case 'contributionlimit':
        if (item.value && item.value.trim().length > 0) {
          stats.contributionLimit = item.value;
        }
        break;
      case 'contributiontaxtreatment':
        if (item.value && item.value.trim().length > 0) {
          stats.taxTreatment = item.value;
        }
        break;
      case 'plantype':
        if (item.value && item.value.trim().length > 0) {
          stats.planTypeValue = item.value;
        }
        break;
      default:
        break;
    }
  });

  return stats;
};
